/* Reading a MEXC futures account with a read-only key.
 *
 * The argument for holding the key at all is in `brokers.ts`. The argument for
 * holding it *here*, on the device, rather than on our Worker, is separate and
 * is the reason this file exists in `src/` instead of in `worker/`:
 *
 *   §3 promises the Worker never sees share counts, cost basis or portfolio
 *   values. A futures balance and a list of open positions is exactly those
 *   things. Routing this through the Worker would break that promise for the
 *   sake of convenience, so the request goes out from the phone and the Mac
 *   and the key never leaves them.
 *
 * That choice has a visible cost, and it is the right one to pay: a device's
 * address changes — a phone moves between networks, a home lease is renewed —
 * so the key cannot be pinned to an IP, which means MEXC expires it after 90
 * days. A key pinned to a fixed address would have to live somewhere with a
 * fixed address, and that somewhere is the Worker. Renewing quarterly is the
 * price of the Worker not knowing.
 *
 * MEXC signs in headers rather than in the query string, so the signature never
 * lands in a URL, a log or a redirect. The secret itself is never transmitted:
 * it keys the HMAC and stays on the device. */

import { brokerGet } from './brokerLink';

const BASE = 'https://contract.mexc.com';

export interface MexcKeys {
  apiKey: string;
  secret: string;
}

/** MEXC signs `accessKey + requestTime + parameters`, where the parameters of a
    GET are its query string with the keys in sorted order. Both of the calls
    below take none, so this is usually the empty string — it is written out
    because a signature that is right by accident is not right. */
export function paramString(params: Record<string, string | number> = {}): string {
  return Object.keys(params)
    .sort()
    .map((k) => `${k}=${params[k]}`)
    .join('&');
}

function toHex(buffer: ArrayBuffer): string {
  return [...new Uint8Array(buffer)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** HMAC-SHA256, from WebCrypto — present in the browser, in the Android WebView
    and in the desktop shell's, and already relied on for the sync envelope. No
    dependency is added for this (§2). */
export async function signRequest(
  apiKey: string, secret: string, requestTime: string, params = '',
): Promise<string> {
  const key = await crypto.subtle.importKey(
    'raw', new TextEncoder().encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'],
  );
  const mac = await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(apiKey + requestTime + params));
  return toHex(mac);
}

/* What the exchange said when it refused.
 *
 * Carried through verbatim, with its code. A refusal that has been reworded
 * into something friendlier is a refusal nobody can look up, and the two things
 * most likely to be wrong here — a key without futures reading enabled, and a
 * key that has passed its ninety days — are told apart by that code alone. */
export class MexcRefused extends Error {
  constructor(readonly code: number, message: string) {
    super(message);
    this.name = 'MexcRefused';
  }
}

interface Envelope {
  success?: boolean;
  code?: number;
  message?: string;
  data?: unknown;
}

async function privateGet(
  keys: MexcKeys, path: string, params: Record<string, string | number>, signal?: AbortSignal,
): Promise<unknown> {
  const requestTime = String(Date.now());
  const query = paramString(params);
  const signature = await signRequest(keys.apiKey, keys.secret, requestTime, query);

  const body = await brokerGet({
    url: `${BASE}${path}${query ? `?${query}` : ''}`,
    headers: {
      'ApiKey': keys.apiKey,
      'Request-Time': requestTime,
      'Signature': signature,
      'Recv-Window': '60',
      'Content-Type': 'application/json',
    },
    signal,
  });

  let envelope: Envelope;
  try {
    envelope = JSON.parse(body) as Envelope;
  } catch {
    throw new Error('MEXC answered with something that was not JSON.');
  }

  if (envelope.success !== true) {
    throw new MexcRefused(
      typeof envelope.code === 'number' ? envelope.code : 0,
      envelope.message ?? 'MEXC refused the request without saying why.',
    );
  }
  return envelope.data;
}

/** Every currency's balance in the futures wallet. */
export async function fetchAssets(keys: MexcKeys, signal?: AbortSignal): Promise<unknown[]> {
  const data = await privateGet(keys, '/api/v1/private/account/assets', {}, signal);
  return Array.isArray(data) ? data : [];
}

/** Every contract currently open. An empty list is an answer, not a failure —
    it means nothing is open, and the screen says that rather than nothing. */
export async function fetchOpenPositions(keys: MexcKeys, signal?: AbortSignal): Promise<unknown[]> {
  const data = await privateGet(keys, '/api/v1/private/position/open_positions', {}, signal);
  return Array.isArray(data) ? data : [];
}

/* One sentence to put under a refusal.
 *
 * Named causes only where the code documents one. Everything else gets the
 * general line, which says what is worth checking without claiming to know
 * which of them it is: a guessed diagnosis reads exactly like a known one and
 * sends someone to fix the wrong thing. The exchange's own code and message are
 * shown either way, so a refusal can always be looked up. */
export function whatToCheck(code: number): string {
  if (code === 401 || code === 602) {
    return 'That is a signature or permission refusal: check the key and the secret are entered whole, with no stray spaces.';
  }
  return "Worth checking, in this order: that reading futures is switched on for this key, that the key has not passed its ninety days, and that this device's clock is right, since the request is signed with its time.";
}
